

import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Grid,
  TextField,
  FormControlLabel,
  Typography,
  Checkbox,
  MenuItem,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Chip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useNavigate, useParams } from "react-router-dom";
import Header from "../../DataLayouts/Header";
import toast from "react-hot-toast";
import { MAIN_URL } from "../../../Configurations/Urls";
import axios from "axios";
import { fetchModules } from "../../../Apis/Modules";
import {
  fetchOrganizationRolePermissions,
  fetchOrganizationRolePermissionsByRole,
  fetchOrganizationUserRolesForSelect,
  saveOrganizationRolePermissions,
} from "../../../Apis/ApplicationManagementApis";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import usePermissionDataStore, {
  notifyPermissionsChanged,
} from "../../../Zustand/Store/usePermissionDataStore";

function RolePermissionForm({ mode }) {
  const { id } = useParams();
  const { userData } = useAuthStore();
  const { refreshPermissions } = usePermissionDataStore();
  const org = userData?.organization;
  const organizationId =
    org?.organization_id ??
    userData?.user_active_organization?.organization_id ??
    userData?.user_active_organization?.user_current_active_organization_id ??
    userData?.activeOrganization?.organization_id;

  const [formData, setFormData] = useState({
    organization_user_role_id: "",
    permissions: [],
  });

  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(mode === "edit");
  const [btnLoading, setbtnLoading] = useState(false);
  const [roles, setRoles] = useState([]);
  const [roleIdsWithPermissions, setRoleIdsWithPermissions] = useState(() => new Set());
  const [modulesWithCategories, setModulesWithCategories] = useState([]);
  const [expandedModules, setExpandedModules] = useState({});

  let navigate = useNavigate();

  const toArray = (value) => {
    if (Array.isArray(value)) return value;
    if (Array.isArray(value?.data)) return value.data;
    return [];
  };

  const getActionPriority = (action) => {
    const raw = String(
      action?.module_action_code || action?.module_action_name || ""
    ).toUpperCase();
    if (raw.includes("VIEW")) return 1;
    if (raw.includes("ADD")) return 2;
    if (raw.includes("EDIT")) return 3;
    if (raw.includes("DELETE")) return 4;
    return 5;
  };

  const sortActions = (actions = []) =>
    [...actions].sort((a, b) => {
      const priorityDiff = getActionPriority(a) - getActionPriority(b);
      if (priorityDiff !== 0) return priorityDiff;
      return String(a?.module_action_name || "").localeCompare(
        String(b?.module_action_name || "")
      );
    });

  const groupActionsByCategory = (actions) => {
    const grouped = {};

    actions.forEach((action) => {
      const category = action.category || "Uncategorized";
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(action);
    });

    return Object.entries(grouped).map(([category_name, actions]) => ({
      category_name,
      actions: sortActions(actions),
    }));
  };

  // Group module actions by feature (fallback: legacy category field)
  const groupActionsByFeature = (module) => {
    if (Array.isArray(module?.features) && module.features.length > 0) {
      const fromFeatures = module.features
        .map((feature) => ({
          category_name:
            feature.module_feature_name ||
            feature.feature_name ||
            "Feature",
          actions: sortActions(feature.actions || []),
        }))
        .filter((group) => group.actions.length > 0);

      if (fromFeatures.length > 0) {
        return fromFeatures;
      }
    }

    return groupActionsByCategory(module.module_action || []);
  };

  // Toggle individual permission
  const togglePermission = (actionId) => {
    setFormData((prev) => {
      const existingIndex = prev.permissions.findIndex(
        (p) => p.application_module_action_id === actionId
      );

      let updatedPermissions;

      if (existingIndex !== -1) {
        updatedPermissions = [...prev.permissions];
        updatedPermissions[existingIndex].permission_allowed =
          !updatedPermissions[existingIndex].permission_allowed;
      } else {
        updatedPermissions = [
          ...prev.permissions,
          { application_module_action_id: actionId, permission_allowed: true },
        ];
      }

      return { ...prev, permissions: updatedPermissions };
    });
  };

  // Check if permission is allowed
  const isPermissionAllowed = (actionId) => {
    const perm = formData.permissions.find(
      (p) => p.application_module_action_id === actionId && p.permission_allowed
    );
    return perm !== undefined;
  };

  // Toggle all permissions in a category
  const toggleCategoryPermissions = (categoryActions, allChecked) => {
    setFormData((prev) => {
      let updatedPermissions = [...prev.permissions];

      categoryActions.forEach((action) => {
        const actionId = action.application_module_action_id;
        const existingIndex = updatedPermissions.findIndex(
          (p) => p.application_module_action_id === actionId
        );

        if (existingIndex !== -1) {
          updatedPermissions[existingIndex].permission_allowed = !allChecked;
        } else {
          updatedPermissions.push({
            application_module_action_id: actionId,
            permission_allowed: !allChecked,
          });
        }
      });

      return { ...prev, permissions: updatedPermissions };
    });
  };

  // Check if all permissions in category are checked
  const isCategoryAllChecked = (categoryActions) => {
    return categoryActions.every((action) =>
      isPermissionAllowed(action.application_module_action_id)
    );
  };

  // Get count of selected permissions in category
  const getCategorySelectedCount = (categoryActions) => {
    return categoryActions.filter((action) =>
      isPermissionAllowed(action.application_module_action_id)
    ).length;
  };

  useEffect(() => {
    if (!organizationId) {
      return;
    }

    fetchOrganizationUserRolesForSelect(organizationId)
      .then((list) => {
        setRoles(list);
        if (list.length === 0) {
          toast.error(
            "No organization roles found. Add roles under User Roles or run role reseed for this organization."
          );
        }
      })
      .catch((err) => {
        console.error("Error fetching roles:", err);
        toast.error(err.message || "Failed to load roles.");
      });
  }, [organizationId]);

  useEffect(() => {
    if (!organizationId) return;

    fetchOrganizationRolePermissions(organizationId)
      .then((res) => {
        const fromApi = Array.isArray(res?.role_ids_with_permissions)
          ? res.role_ids_with_permissions
          : [];
        if (fromApi.length) {
          setRoleIdsWithPermissions(
            new Set(fromApi.map((id) => Number(id)).filter((id) => id > 0))
          );
          return;
        }

        const rows = Array.isArray(res?.permissions)
          ? res.permissions
          : Array.isArray(res?.permissions?.data)
            ? res.permissions.data
            : [];
        const ids = new Set();
        rows.forEach((row) => {
          const roleId = Number(
            row?.organization_user_role_id ??
              row?.organization_user_role?.organization_user_role_id
          );
          if (roleId > 0) ids.add(roleId);
        });
        setRoleIdsWithPermissions(ids);
      })
      .catch(() => setRoleIdsWithPermissions(new Set()));
  }, [organizationId]);

  const availableRoles = useMemo(() => {
    const list = toArray(roles);
    if (mode === "edit") return list;
    return list.filter(
      (role) => !roleIdsWithPermissions.has(Number(role.organization_user_role_id))
    );
  }, [roles, roleIdsWithPermissions, mode]);

  // Fetch modules with actions and group by category
  useEffect(() => {
    fetchModules()
      .then((data) => {
        const modules = toArray(data?.modules);

        const transformedModules = modules.map((module) => ({
          module_id: module.module_id || module.application_module_id,
          module_name: module.module_name,
          categories: groupActionsByFeature(module),
        }));

        console.log("Transformed modules with categories:", transformedModules);
        setModulesWithCategories(transformedModules);

        // Expand all modules by default
        const expanded = {};
        transformedModules.forEach((module) => {
          expanded[module.module_id] = true;
        });
        setExpandedModules(expanded);
      })
      .catch((err) => {
        console.error("Error fetching modules:", err);
        toast.error("Failed to load modules.");
      });
  }, []);

  // Fetch existing permissions in edit mode
  useEffect(() => {
    const getPermissionsByRoleId = async () => {
      try {
        console.log("Fetching permissions for role:", id);
        const response = await fetchOrganizationRolePermissionsByRole(
          organizationId,
          id
        );

        const roleId = response.organization_user_role_id;
        const permissions = response.permissions || [];

        const transformedPermissions = permissions.map((perm) => ({
          application_module_action_id: perm.application_module_action_id,
          permission_allowed: Boolean(perm.permission_allowed),
        }));

        setFormData({
          organization_user_role_id:
            roleId != null ? String(roleId) : "",
          permissions: transformedPermissions,
        });
      } catch (err) {
        console.error("Failed to fetch role permissions", err);
        if (err.response?.status === 404) {
          toast.error("No permissions found for this role.");
        } else {
          toast.error("Failed to load role permissions.");
        }
      } finally {
        setLoading(false);
      }
    };

    if (mode === "edit" && id) {
      setLoading(true);
      getPermissionsByRoleId();
    }
  }, [mode, id, organizationId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    const errors = {};
    const selectedRoleId = Number(formData.organization_user_role_id);

    if (!formData.organization_user_role_id) {
      errors.organization_user_role_id = "User Role is required.";
    } else if (
      mode === "add" &&
      selectedRoleId > 0 &&
      roleIdsWithPermissions.has(selectedRoleId)
    ) {
      errors.organization_user_role_id =
        "Permissions already exist for this role. Use View Permissions to edit.";
    }

    setFormErrors(errors);
    return Object.keys(errors)?.length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    if (!organizationId) {
      toast.error("Organization ID is not available. Please refresh the page.");
      return;
    }

    setbtnLoading(true);
    try {
      const payload = {
        organization_user_role_id: Number(formData.organization_user_role_id),
        permissions: formData.permissions || [],
        is_new_assignment: mode === "add",
      };

      await saveOrganizationRolePermissions(organizationId, payload);

      toast.success(
        mode === "edit" ? "Permissions Updated!" : "Permissions Saved!"
      );

      // Refresh permissions in the store if updating current user's role
      if (userData?.metadata?.[0]?.organization_user_id) {
        await refreshPermissions(userData.metadata[0].organization_user_id);
      }

      notifyPermissionsChanged();

      setFormErrors({});
      navigate(-1);
    } catch (err) {
      console.error("Submit error:", err);
      if (err.response?.status === 422) {
        const validationErrors = err.response.data.errors || {};
        setFormErrors(validationErrors);
        const errorMessages =
          err.response.data?.error ||
          Object.values(validationErrors)
            .map((arr) => (Array.isArray(arr) ? arr[0] : arr))
            .filter(Boolean)
            .join(" ");
        toast.error(errorMessages || "Validation failed.");
      } else {
        toast.error(err.response?.data?.error || "Something went wrong.");
      }
    } finally {
      setbtnLoading(false);
    }
  };

  const handleAccordionChange = (moduleId) => {
    setExpandedModules((prev) => ({
      ...prev,
      [moduleId]: !prev[moduleId],
    }));
  };

  if (!organizationId && !loading) {
    return (
      <Header
        mode={mode}
        updateMessage={"User Role Permission"}
        addMessage={"User Role Permission"}
        homeLink={"/application/user-role-permission"}
        homeText={"User Role Permission"}
        hideFooter
      >
        <Box mt={2} p={2} bgcolor="#fff3cd" borderRadius={1}>
          <Typography color="warning.main">
            Organization information is not available. Please refresh the page or log in again.
          </Typography>
        </Box>
      </Header>
    );
  }

  return (
      <Header
        mode={mode}
        updateMessage={"User Role Permission"}
        addMessage={"User Role Permission"}
        homeLink={"/application/user-role-permission"}
        homeText={"User Role Permission"}
        onSubmit={handleSubmit}
        loading={loading}
        btnLoading={btnLoading}
      >

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12} md={10}>
            {/* Role Selection */}
            <TextField
              select
              fullWidth
              label="User Role"
              name="organization_user_role_id"
              value={formData.organization_user_role_id}
              onChange={handleChange}
              error={!!formErrors.organization_user_role_id}
              helperText={
                formErrors.organization_user_role_id ||
                (mode === "add" && availableRoles.length === 0
                  ? "All roles already have permissions. Use View Permissions on the list to edit."
                  : roles.length === 0
                    ? "No organization roles available. Create roles under User Roles first."
                    : mode === "add"
                      ? "Roles that already have permissions are hidden."
                      : "")
              }
              required
              disabled={
                mode === "edit" ||
                roles.length === 0 ||
                (mode === "add" && availableRoles.length === 0)
              }
              sx={{ mb: 3 }}
            >
              {availableRoles.map((option) => (
                <MenuItem
                  key={option.organization_user_role_id}
                  value={option.organization_user_role_id}
                >
                  {option?.user_role_name}
                </MenuItem>
              ))}
            </TextField>

            {/* Debug Info */}
            {formData.organization_user_role_id && (
              <Box mb={2} p={2} bgcolor="#f5f5f5" borderRadius={1}>
                {/* <Typography variant="caption" display="block">
                  <strong>Debug:</strong> Loaded {formData.permissions.length} permissions
                </Typography> */}
                <Typography variant="caption" display="block">
                  Allowed Permissions: {formData.permissions.filter((p) => p.permission_allowed).length}
                </Typography>
              </Box>
            )}

            {/* Module Permissions with Categories */}
            <Box mt={2}>
              {modulesWithCategories.length === 0 ? (
                <Box textAlign="center" py={4}>
                  <Typography color="text.secondary">
                    No modules found. Please add modules first.
                  </Typography>
                </Box>
              ) : (
                modulesWithCategories.map((module) => (
                  <Accordion
                    key={module.module_id}
                    expanded={expandedModules[module.module_id]}
                    onChange={() => handleAccordionChange(module.module_id)}
                    sx={{
                      mb: 2,
                      border: "1px solid #e0e0e0",
                      borderRadius: "8px !important",
                      "&:before": { display: "none" },
                      boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        backgroundColor: "#f5f5f5",
                        borderRadius: "8px",
                        "&.Mui-expanded": {
                          borderBottomLeftRadius: 0,
                          borderBottomRightRadius: 0,
                        },
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={2} width="100%">
                        <Typography variant="h6" fontWeight={600}>
                          {module.module_name || "Unnamed Module"}
                        </Typography>
                        <Chip
                          label={`${module.categories.reduce(
                            (acc, cat) => acc + getCategorySelectedCount(cat.actions),
                            0
                          )} selected`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 3 }}>
                      {module.categories?.length === 0 ? (
                        <Typography color="text.secondary">
                          No actions available for this module.
                        </Typography>
                      ) : (
                        module.categories?.map((category, catIndex) => (
                          <Box key={catIndex} mb={3}>
                            <Box
                              display="flex"
                              alignItems="center"
                              justifyContent="space-between"
                              mb={2}
                              sx={{
                                backgroundColor: "#f9f9f9",
                                p: 2,
                                borderRadius: 1,
                                border: "1px solid #e0e0e0",
                              }}
                            >
                              <Box display="flex" alignItems="center" gap={2}>
                                <Typography
                                  variant="subtitle1"
                                  fontWeight={500}
                                  color="primary"
                                >
                                  {category.category_name}
                                </Typography>
                                <Chip
                                  label={`${getCategorySelectedCount(
                                    category.actions
                                  )}/${category.actions.length}`}
                                  size="small"
                                  color={
                                    isCategoryAllChecked(category.actions)
                                      ? "success"
                                      : "default"
                                  }
                                  variant="filled"
                                />
                              </Box>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() =>
                                  toggleCategoryPermissions(
                                    category.actions,
                                    isCategoryAllChecked(category.actions)
                                  )
                                }
                                disabled={mode === "view"}
                                sx={{
                                  textTransform: "none",
                                  borderRadius: 1,
                                }}
                              >
                                {isCategoryAllChecked(category.actions)
                                  ? "Uncheck All"
                                  : "Check All"}
                              </Button>
                            </Box>

                            <Grid container spacing={2}>
                              {category.actions?.map((action) => (
                                <Grid
                                  item
                                  xs={12}
                                  sm={6}
                                  md={4}
                                  key={action.application_module_action_id}
                                >
                                  <Box
                                    sx={{
                                      p: 1.5,
                                      border: "1px solid #e0e0e0",
                                      borderRadius: 1,
                                      backgroundColor: isPermissionAllowed(
                                        action.application_module_action_id
                                      )
                                        ? "#e3f2fd"
                                        : "#fff",
                                      transition: "all 0.2s",
                                      "&:hover": {
                                        backgroundColor: isPermissionAllowed(
                                          action.application_module_action_id
                                        )
                                          ? "#bbdefb"
                                          : "#f5f5f5",
                                        borderColor: "#1976d2",
                                      },
                                    }}
                                  >
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          checked={isPermissionAllowed(
                                            action.application_module_action_id
                                          )}
                                          onChange={() =>
                                            togglePermission(
                                              action.application_module_action_id
                                            )
                                          }
                                          disabled={mode === "view"}
                                        />
                                      }
                                      label={
                                        <Box>
                                          <Typography variant="body2" fontWeight={500}>
                                            {action.module_action_name}
                                          </Typography>
                                          {action.module_action_code && (
                                            <Typography
                                              variant="caption"
                                              color="text.secondary"
                                              sx={{ display: "block", mt: 0.5 }}
                                            >
                                              {/* {action.module_action_code} */}
                                            </Typography>
                                          )}
                                        </Box>
                                      }
                                    />
                                  </Box>
                                </Grid>
                              ))}
                            </Grid>

                            {catIndex < module.categories.length - 1 && (
                              <Divider sx={{ mt: 3 }} />
                            )}
                          </Box>
                        ))
                      )}
                    </AccordionDetails>
                  </Accordion>
                ))
              )}
            </Box>

            {/* Action Buttons */}
            <Box mt={4} display="flex" gap={2}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={loading || btnLoading || mode === "view"}
                sx={{
                  borderRadius: 2,
                  minWidth: 120,
                  textTransform: "capitalize",
                  fontWeight: 500,
                  px: 4,
                  py: 1.5,
                }}
              >
                {loading || btnLoading ? (
                  <CircularProgress size={22} sx={{ color: "#fff" }} />
                ) : (
                  "Submit"
                )}
              </Button>

              <Button
                variant="outlined"
                color="primary"
                size="medium"
                onClick={() => navigate(-1)}
                sx={{
                  borderRadius: 2,
                  minWidth: 120,
                  textTransform: "capitalize",
                  fontWeight: 500,
                  px: 4,
                  py: 1.5,
                }}
              >
                Cancel
              </Button>
            </Box>
          </Grid>
        </Grid>
      )}
    </Header>
  );
}

export default RolePermissionForm;